class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.productContainer = document.getElementById('cart-notification-product');
    this.header = document.querySelector('sticky-header');
    this.overlay = this.querySelector('.rc-cart-overlay');

    this.onCartIconClick = this.handleCartIconClick.bind(this);

    this.controllerProductHandle = 'racechip-xlr-5-smart-controller';
    this.controllerProduct = null;

    if (!this.notification) return;

    this.notification.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.querySelectorAll('.cart-notification__close').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('.rc-cart-drawer__continue').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    this.bindCartIcon();
    this.installCartAddWatcher();
  }

  /* =========================================================
     BASIC CART
     ========================================================= */

  bindCartIcon() {
    const cartIcon = document.getElementById('cart-icon-bubble');

    if (!cartIcon) return;

    cartIcon.removeEventListener('click', this.onCartIconClick);
    cartIcon.addEventListener('click', this.onCartIconClick);
  }

  async handleCartIconClick(event) {
    event.preventDefault();

    try {
      await this.refreshCart();
      this.open();
    } catch (error) {
      console.error('RaceChip cart drawer:', error);
      window.location.href = '/cart';
    }
  }

  installCartAddWatcher() {
    if (window.__raceChipCartWatcherInstalled) return;

    window.__raceChipCartWatcherInstalled = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      try {
        const request = args[0];

        const url =
          typeof request === 'string'
            ? request
            : request && request.url
              ? request.url
              : '';

        const isCartAdd = url.includes('/cart/add');

        if (
          isCartAdd &&
          response.ok &&
          !window.__raceChipDrawerMutation
        ) {
          setTimeout(async () => {
            const drawer = document.querySelector('cart-notification');

            if (!drawer) return;

            try {
              await drawer.refreshCart();
              drawer.open();
            } catch (error) {
              console.error('RaceChip cart refresh:', error);
            }
          }, 120);
        }
      } catch (error) {
        console.error('RaceChip cart watcher:', error);
      }

      return response;
    };
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Kunde inte läsa varukorgen');
    }

    return await response.json();
  }

  async getControllerProduct() {
    if (this.controllerProduct) {
      return this.controllerProduct;
    }

    const response = await fetch(
      `/products/${this.controllerProductHandle}.js`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      }
    );

    if (!response.ok) {
      throw new Error('Kunde inte läsa XLR Smart Controller');
    }

    const product = await response.json();

    const variant =
      product.variants.find((item) => item.available) ||
      product.variants[0];

    if (!variant) {
      throw new Error('Ingen variant hittades för XLR Smart Controller');
    }

    this.controllerProduct = {
      productId: Number(product.id || 0),
      variantId: Number(variant.id),
      price: Number(variant.price || 0),
      available: Boolean(variant.available),
      title: 'Smart Controller'
    };

    return this.controllerProduct;
  }

  async refreshCart() {
    let cart = await this.getCart();

    cart = await this.cleanupOrphanAccessories(cart);

    let controllerProduct = null;

    try {
      const hasXlr = cart.items.some((item) => this.isXlrItem(item));

      if (hasXlr) {
        controllerProduct = await this.getControllerProduct();
      }
    } catch (error) {
      console.error('RaceChip controller product:', error);
    }

    this.renderCart(cart, controllerProduct);
    this.updateCartBubble(cart.item_count);

    return cart;
  }

  async cleanupOrphanAccessories(cart) {
    if (!cart?.items?.length) return cart;

    const mainConfigIds = new Set(
      cart.items
        .filter((item) => {
          const family = item.properties?._rc_family || '';

          return (
            family === 's' ||
            family === 'rs' ||
            family === 'gts-5' ||
            family === 'gts-5-black' ||
            family === 'xlr5'
          );
        })
        .map((item) =>
          String(
            item.properties?._rc_config_id ||
            item.properties?._rc_base_variant_id ||
            item.variant_id
          )
        )
    );

    const xlrConfigIds = new Set(
      cart.items
        .filter((item) => this.isXlrItem(item))
        .map((item) =>
          String(
            item.properties?._rc_parent_config_id ||
            item.properties?._rc_config_id ||
            ''
          )
        )
        .filter(Boolean)
    );

    const updates = {};

    cart.items.forEach((item) => {
      const properties = item.properties || {};
      const parentConfigId = String(properties._rc_parent_config_id || '');

      if (this.isSmartItem(item)) {
        const hasValidParent =
          parentConfigId && mainConfigIds.has(parentConfigId);

        if (!hasValidParent) {
          updates[item.key] = 0;
        }
      }

      if (this.isControllerItem(item)) {
        const hasValidParent =
          parentConfigId &&
          mainConfigIds.has(parentConfigId) &&
          xlrConfigIds.has(parentConfigId);

        if (!hasValidParent) {
          updates[item.key] = 0;
        }
      }
    });

    if (!Object.keys(updates).length) {
      return cart;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error('Kunde inte rensa gamla tillbehör från varukorgen');
    }

    return await response.json();
  }

  /* =========================================================
     RENDER CART
     ========================================================= */

  renderCart(cart, controllerProduct = null) {
    if (!this.productContainer) return;

    if (!cart.items || cart.items.length === 0) {
      this.productContainer.innerHTML = `
        <div class="rc-drawer-empty">
          Din varukorg är tom.
        </div>

        <div class="rc-drawer-total">
          <span>Totalt</span>
          <strong>${this.money(0)}</strong>
        </div>
      `;

      this.updateCartBubble(0);
      return;
    }

    const configurations = this.getConfigurations(
      cart,
      controllerProduct
    );

    const visibleItems = cart.items.filter((item) => {
      return (
        !this.isSmartItem(item) &&
        !this.isControllerItem(item, controllerProduct)
      );
    });

    const itemsHTML = visibleItems
      .map((item) => this.renderCartItem(item, configurations))
      .join('');

    const upsellsHTML = configurations.length
      ? configurations
          .map((config) => this.renderUpsells(config))
          .join('')
      : '';

    this.productContainer.innerHTML = `
      <div class="rc-drawer-items">
        ${itemsHTML}
      </div>

      ${upsellsHTML}

      <div class="rc-drawer-total">
        <span>Totalt</span>
        <strong>${this.money(cart.total_price)}</strong>
      </div>
    `;

    this.bindItemControls();
    this.bindUpsells();
  }

  /* =========================================================
     CONFIGURATION
     ========================================================= */

  getConfigurations(cart, controllerProduct = null) {
    const mainItems = cart.items.filter((item) => {
      const family = item.properties?._rc_family || '';

      return (
        family === 's' ||
        family === 'rs' ||
        family === 'gts-5' ||
        family === 'gts-5-black' ||
        family === 'xlr5'
      );
    });

    const claimedLegacyXlrKeys = new Set();

    return mainItems.map((mainItem) => {
      const properties = mainItem.properties || {};
      const family = properties._rc_family || '';

      const appVariantId = Number(properties._rc_app_variant_id || 0);
      const baseVariantId = Number(properties._rc_base_variant_id || 0);
      const smartVariantId = Number(properties._rc_smart_variant_id || 0);
      const xlrVariantId = Number(properties._rc_xlr_variant_id || 0);

      const car =
        properties['Bil'] ||
        properties['BIL'] ||
        properties['bil'] ||
        '';

      const motor =
        properties['Motor'] ||
        properties['MOTOR'] ||
        properties['motor'] ||
        '';

      const shortCar = this.getShortCarName(car, motor);

      const configId = String(
        properties._rc_config_id ||
        baseVariantId ||
        mainItem.variant_id
      );

      const appSelected =
        properties._rc_app_selected === 'true' ||
        (
          appVariantId > 0 &&
          Number(mainItem.variant_id) === appVariantId
        );

      const smartItem = cart.items.find((item) => {
        const itemProperties = item.properties || {};

        return (
          this.isSmartItem(item) &&
          itemProperties._rc_parent_config_id &&
          String(itemProperties._rc_parent_config_id) === configId
        );
      });

      const smartSelected = Boolean(smartItem);

      let xlrItem =
        family === 'xlr5'
          ? mainItem
          : cart.items.find((item) => {
              const itemProperties = item.properties || {};

              return (
                this.isXlrItem(item) &&
                itemProperties._rc_parent_config_id &&
                String(itemProperties._rc_parent_config_id) === configId
              );
            });

      if (!xlrItem && xlrVariantId > 0) {
        xlrItem = cart.items.find((item) => {
          const itemProperties = item.properties || {};
          const hasParent = Boolean(itemProperties._rc_parent_config_id);

          return (
            !hasParent &&
            !claimedLegacyXlrKeys.has(item.key) &&
            this.isXlrItem(item) &&
            Number(item.variant_id) === xlrVariantId
          );
        });

        if (xlrItem) {
          claimedLegacyXlrKeys.add(xlrItem.key);
        }
      }

      const xlrSelected = Boolean(xlrItem);

      const controllerItem = cart.items.find((item) => {
        const itemProperties = item.properties || {};

        return (
          this.isControllerItem(item, controllerProduct) &&
          itemProperties._rc_parent_config_id &&
          String(itemProperties._rc_parent_config_id) === configId
        );
      });

      const controllerSelected = Boolean(controllerItem);

      const appSupported =
        properties._rc_app_supported === 'true' &&
        appVariantId > 0 &&
        baseVariantId > 0;

      const smartSupported =
        (
          family === 'gts-5' ||
          family === 'gts-5-black'
        ) &&
        properties._rc_smart_supported === 'true' &&
        smartVariantId > 0;

      const xlrSupported =
        family !== 'xlr5' &&
        properties._rc_xlr_supported === 'true' &&
        xlrVariantId > 0;

      const controllerSupported =
        xlrSelected &&
        controllerProduct &&
        controllerProduct.variantId > 0 &&
        controllerProduct.available;

      const basePrice = Number(properties._rc_base_price || 0);
      const appPrice = Number(properties._rc_app_price || 0);

      return {
        mainItem,
        properties,
        family,
        configId,
        car,
        motor,
        shortCar,

        baseVariantId,
        appVariantId,

        appSupported,
        appSelected,
        appPriceDelta: Math.max(0, appPrice - basePrice),

        smartSupported,
        smartSelected,
        smartVariantId,
        smartPrice: Number(properties._rc_smart_price || 0),
        smartItem,

        xlrSupported,
        xlrSelected,
        xlrVariantId,
        xlrPrice: Number(properties._rc_xlr_price || 0),
        xlrTitle: properties._rc_xlr_title || 'RaceChip XLR 5',
        xlrItem,

        controllerSupported,
        controllerSelected,
        controllerVariantId: Number(controllerProduct?.variantId || 0),
        controllerPrice: Number(controllerProduct?.price || 0),
        controllerItem
      };
    });
  }

  isSmartItem(item) {
    const title =
      `${item.product_title || ''} ${item.title || ''}`.toLowerCase();

    return (
      Number(item.variant_id) === 59377643487557 ||
      title.includes('smart functions')
    );
  }

  isXlrItem(item) {
    if (item.properties?._rc_accessory === 'xlr') {
      return true;
    }

    const title =
      `${item.product_title || ''} ${item.title || ''}`.toLowerCase();

    return (
      title.includes('xlr') &&
      !title.includes('controller')
    );
  }

  isControllerItem(item, controllerProduct = null) {
    if (item.properties?._rc_accessory === 'xlr-controller') {
      return true;
    }

    if (
      controllerProduct?.variantId &&
      Number(item.variant_id) === Number(controllerProduct.variantId)
    ) {
      return true;
    }

    const title =
      `${item.product_title || ''} ${item.title || ''}`.toLowerCase();

    return (
      title.includes('controller') &&
      title.includes('xlr')
    );
  }

  /* =========================================================
     PRODUCT ROW
     ========================================================= */

  renderCartItem(item, configurations = []) {
    const properties = item.properties || {};

    const image =
      item.featured_image?.url ||
      item.image ||
      '';

    const car =
      properties['Bil'] ||
      properties['BIL'] ||
      properties['bil'] ||
      properties._rc_parent_car ||
      '';

    const motor =
      properties['Motor'] ||
      properties['MOTOR'] ||
      properties['motor'] ||
      '';

    let shortCar = this.getShortCarName(car, motor);

    if (this.isXlrItem(item)) {
      const parentConfigId = String(
        properties._rc_parent_config_id || ''
      );

      const parentConfig = configurations.find((config) => {
        if (
          parentConfigId &&
          String(config.configId) === parentConfigId
        ) {
          return true;
        }

        if (
          Number(config.xlrVariantId || 0) > 0 &&
          Number(config.xlrVariantId) === Number(item.variant_id)
        ) {
          return true;
        }

        return false;
      });

      if (parentConfig?.shortCar) {
        shortCar = parentConfig.shortCar;
      }
    }

    const power =
      properties['Effektökning'] ||
      properties['Effektokning'] ||
      properties['EFFEKTÖKNING'] ||
      '';

    const title = this.cleanProductTitle(
      item.product_title || item.title || ''
    );

    const family = properties._rc_family || '';

    const isMain =
      family === 's' ||
      family === 'rs' ||
      family === 'gts-5' ||
      family === 'gts-5-black' ||
      family === 'xlr5';

    const appVariantId = Number(properties._rc_app_variant_id || 0);

    const hasApp =
      properties._rc_app_selected === 'true' ||
      (
        appVariantId > 0 &&
        Number(item.variant_id) === appVariantId
      );

    const itemConfig = isMain
      ? configurations.find((config) => config.mainItem?.key === item.key)
      : null;

    const hasSmart = Boolean(itemConfig?.smartSelected);
    const hasXlr = Boolean(itemConfig?.xlrSelected);
    const hasController = Boolean(itemConfig?.controllerSelected);

    return `
      <div
        class="rc-drawer-item"
        data-key="${this.escapeHTML(item.key)}"
      >

        ${
          image
            ? `
              <div class="rc-drawer-item__image">
                <img
                  src="${this.escapeHTML(image)}"
                  alt="${this.escapeHTML(title)}"
                  loading="lazy"
                >
              </div>
            `
            : `
              <div class="rc-drawer-item__image rc-drawer-item__image--empty"></div>
            `
        }

        <div class="rc-drawer-item__info">

          <div class="rc-drawer-item__top">

            <div class="rc-drawer-item__copy">

              <div class="rc-drawer-item__title">
                ${this.escapeHTML(title)}
              </div>

              ${
                shortCar
                  ? `
                    <div class="rc-drawer-car">
                      ${this.escapeHTML(shortCar)}
                    </div>
                  `
                  : ''
              }

              ${
                power
                  ? `
                    <div class="rc-drawer-power">
                      ${this.escapeHTML(String(power))}
                    </div>
                  `
                  : ''
              }

              ${
                isMain && hasApp
                  ? `
                    <div class="rc-drawer-option">
                      <span class="rc-drawer-check">✓</span>
                      Appstyrning
                    </div>
                  `
                  : ''
              }

              ${
                isMain && hasSmart
                  ? `
                    <div class="rc-drawer-option">
                      <span class="rc-drawer-check">✓</span>
                      SMART Functions
                    </div>
                  `
                  : ''
              }

              ${
                isMain && hasXlr
                  ? `
                    <div class="rc-drawer-option">
                      <span class="rc-drawer-check">✓</span>
                      RaceChip XLR 5
                    </div>
                  `
                  : ''
              }

              ${
                isMain && hasXlr && hasController
                  ? `
                    <div class="rc-drawer-option rc-drawer-option--sub">
                      <span class="rc-drawer-check">✓</span>
                      Smart Controller
                    </div>
                  `
                  : ''
              }

            </div>

            <div class="rc-drawer-item__price">
              ${this.money(item.final_line_price)}
            </div>

          </div>

          <div class="rc-drawer-item__controls">

            <div class="rc-drawer-quantity">

              <button
                type="button"
                class="rc-drawer-qty-button"
                data-action="decrease"
                data-key="${this.escapeHTML(item.key)}"
                data-quantity="${item.quantity}"
                aria-label="Minska antal"
              >
                −
              </button>

              <span class="rc-drawer-qty-value">
                ${item.quantity}
              </span>

              <button
                type="button"
                class="rc-drawer-qty-button"
                data-action="increase"
                data-key="${this.escapeHTML(item.key)}"
                data-quantity="${item.quantity}"
                aria-label="Öka antal"
              >
                +
              </button>

            </div>

            <button
              type="button"
              class="rc-drawer-remove"
              data-key="${this.escapeHTML(item.key)}"
              aria-label="Ta bort ${this.escapeHTML(title)}"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

          </div>

        </div>

      </div>
    `;
  }

  getShortCarName(car, motor) {
    const carText = String(car || '').trim();
    const motorText = String(motor || '').trim();

    const brandMatch = carText.match(
      /\b(BMW|Audi|Mercedes(?:-Benz)?|Volkswagen|VW|Porsche|Volvo|Alfa Romeo|Abarth|MINI|Cupra|SEAT|Skoda|Škoda|Ford|Opel|Peugeot|Renault|Citroën|Citroen|Fiat|Toyota|Lexus|Honda|Mazda|Nissan|Kia|Hyundai|Land Rover|Range Rover|Jaguar)\b/i
    );

    let brand = brandMatch ? brandMatch[1] : '';

    if (/^mercedes/i.test(brand)) brand = 'Mercedes-Benz';
    if (/^vw$/i.test(brand)) brand = 'Volkswagen';

    let engine = motorText
      .replace(/\s*\([^)]*(?:HP|kW)[^)]*\).*$/i, '')
      .replace(/\s*\([^)]*\)\s*$/i, '')
      .trim();

    if (!engine) {
      const matches = carText.match(
        /\b([A-Z]?\d{2,4}[a-zA-Z]*(?:\s+(?:xDrive|sDrive|Mild-Hybrid|Hybrid|TFSI|TDI|TSI|AMG|CDI|d|i|e))?)\b/g
      );

      if (matches && matches.length) {
        engine = matches[matches.length - 1];
      }
    }

    if (brand && engine) {
      return `${brand} ${engine}`.trim();
    }

    if (engine) return engine;
    if (brand) return brand;

    return carText;
  }

  cleanProductTitle(title) {
    if (/gts\s*5\s*black/i.test(title)) return 'RaceChip GTS 5 Black';
    if (/gts\s*5/i.test(title)) return 'RaceChip GTS 5';
    if (/racechip\s*rs/i.test(title)) return 'RaceChip RS';
    if (/racechip\s*s(?!mart)/i.test(title)) return 'RaceChip S';
    if (/xlr\s*5/i.test(title)) return 'RaceChip XLR 5';

    return title;
  }

  /* =========================================================
     PERMANENT UPSELL TOGGLES
     ========================================================= */

  renderUpsells(config) {
    const rows = [];

    if (config.appSupported) {
      rows.push(
        this.renderUpsellRow({
          type: 'app',
          title: 'Appstyrning',
          price: config.appPriceDelta,
          selected: config.appSelected,
          configId: config.configId,
          car: config.shortCar
        })
      );
    }

    if (config.smartSupported) {
      rows.push(
        this.renderUpsellRow({
          type: 'smart',
          title: 'SMART Functions',
          price: config.smartPrice,
          selected: config.smartSelected,
          configId: config.configId,
          car: config.shortCar
        })
      );
    }

    if (config.xlrSupported) {
      rows.push(
        this.renderUpsellRow({
          type: 'xlr',
          title: config.xlrTitle,
          price: config.xlrPrice,
          selected: config.xlrSelected,
          configId: config.configId,
          car: config.shortCar
        })
      );
    }

    if (config.controllerSupported) {
      rows.push(
        this.renderUpsellRow({
          type: 'controller',
          title: 'Smart Controller',
          price: config.controllerPrice,
          selected: config.controllerSelected,
          configId: config.configId,
          car: config.shortCar
        })
      );
    }

    if (!rows.length) return '';

    return `
      <div
        class="rc-drawer-upsells"
        data-config-id="${this.escapeHTML(config.configId)}"
      >

        <div class="rc-drawer-upsells__title">
          LÄGG TILL TILLBEHÖR
          ${
            config.shortCar
              ? `<span class="rc-drawer-upsells__car">${this.escapeHTML(config.shortCar)}</span>`
              : ''
          }
        </div>

        <div class="rc-drawer-upsells__list">
          ${rows.join('')}
        </div>

      </div>
    `;
  }

  renderUpsellRow({ type, title, price, selected, configId, car }) {
    return `
      <button
        type="button"
        class="rc-drawer-upsell${selected ? ' rc-drawer-upsell--selected' : ''}"
        data-type="${type}"
        data-config-id="${this.escapeHTML(configId)}"
        aria-pressed="${selected ? 'true' : 'false'}"
      >

        <span class="rc-drawer-upsell__checkbox">
          ${selected ? '✓' : ''}
        </span>

        <span class="rc-drawer-upsell__icon">
          ${this.getUpsellIcon(type)}
        </span>

        <span class="rc-drawer-upsell__name">
          ${this.escapeHTML(title)}
          ${
            car
              ? `<small class="rc-drawer-upsell__car">${this.escapeHTML(car)}</small>`
              : ''
          }
        </span>

        <span class="rc-drawer-upsell__price">
          +${this.money(price)}
        </span>

      </button>
    `;
  }

  getUpsellIcon(type) {
    if (type === 'app') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="7"
            y="2.5"
            width="10"
            height="19"
            rx="2"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="M10 5h4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <circle cx="12" cy="18.5" r="0.8" fill="currentColor"/>
        </svg>
      `;
    }

    if (type === 'smart') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="14" width="4" height="7" rx="1" fill="currentColor"/>
          <rect x="10" y="9" width="4" height="12" rx="1" fill="currentColor"/>
          <rect x="17" y="4" width="4" height="17" rx="1" fill="currentColor"/>
        </svg>
      `;
    }

    if (type === 'xlr') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4.2 18a8.5 8.5 0 1 1 15.6 0"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <path
            d="M12 12l4.8-3.2"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
          <circle cx="12" cy="12" r="1.4" fill="currentColor"/>
          <path
            d="M5.5 13H3.8M7.3 8.4 6 7.2M12 5.5V3.8M16.7 8.4 18 7.2M18.5 13h1.7"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
        </svg>
      `;
    }

    if (type === 'controller') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="5"
            y="3"
            width="14"
            height="18"
            rx="3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
          />
          <circle
            cx="12"
            cy="9"
            r="2.4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M8.5 15h7M10 18h4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      `;
    }

    return '';
  }

  /* =========================================================
     BIND TOGGLES
     ========================================================= */

  bindUpsells() {
    this.productContainer
      .querySelectorAll('.rc-drawer-upsell')
      .forEach((button) => {
        button.addEventListener('click', async () => {
          if (button.disabled) return;

          button.disabled = true;
          button.classList.add('rc-drawer-upsell--loading');

          window.__raceChipDrawerMutation = true;

          try {
            const cart = await this.getCart();

            let controllerProduct = null;

            try {
              const hasXlr = cart.items.some((item) => this.isXlrItem(item));

              if (hasXlr || button.dataset.type === 'xlr') {
                controllerProduct = await this.getControllerProduct();
              }
            } catch (error) {
              console.error('RaceChip controller:', error);
            }

            const configurations = this.getConfigurations(
              cart,
              controllerProduct
            );

            const configId = String(button.dataset.configId || '');

            const config = configurations.find(
              (item) => String(item.configId) === configId
            );

            if (!config) {
              throw new Error('RaceChip-konfiguration saknas');
            }

            const type = button.dataset.type;

            if (type === 'app') {
              await this.toggleApp(config);
            }

            if (type === 'smart') {
              await this.toggleSmart(config);
            }

            if (type === 'xlr') {
              await this.toggleXlr(config);
            }

            if (type === 'controller') {
              await this.toggleController(config);
            }

            await this.refreshCart();
          } catch (error) {
            console.error('RaceChip tillval:', error);

            button.disabled = false;
            button.classList.remove('rc-drawer-upsell--loading');
          } finally {
            window.__raceChipDrawerMutation = false;
          }
        });
      });
  }

  /* =========================================================
     APP TOGGLE
     ========================================================= */

  async toggleApp(config) {
    if (config.appSelected) {
      if (config.smartSelected && config.smartItem) {
        await this.removeCartLine(config.smartItem.key);
      }

      const properties = {
        ...config.properties,
        _rc_config_id: config.configId,
        _rc_app_selected: 'false',
        _rc_smart_selected: 'false'
      };

      await this.replaceMainVariant(
        config.mainItem,
        config.baseVariantId,
        properties
      );

      return;
    }

    const properties = {
      ...config.properties,
      _rc_config_id: config.configId,
      _rc_app_selected: 'true'
    };

    await this.replaceMainVariant(
      config.mainItem,
      config.appVariantId,
      properties
    );
  }

  /* =========================================================
     SMART FUNCTIONS TOGGLE
     ========================================================= */

  async toggleSmart(config) {
    if (config.smartSelected) {
      if (config.smartItem) {
        await this.removeCartLine(config.smartItem.key);
      }

      return;
    }

    if (!config.appSelected) {
      const properties = {
        ...config.properties,
        _rc_config_id: config.configId,
        _rc_app_selected: 'true',
        _rc_smart_selected: 'true'
      };

      await this.replaceMainVariant(
        config.mainItem,
        config.appVariantId,
        properties
      );
    }

    await this.addCartItem(
      config.smartVariantId,
      config.mainItem.quantity,
      {
        _rc_accessory: 'smart',
        _rc_parent_config_id: config.configId,
        _rc_parent_car: config.shortCar,
        _rc_smart_selected: 'true'
      }
    );
  }

  /* =========================================================
     XLR TOGGLE
     ========================================================= */

  async toggleXlr(config) {
    if (config.xlrSelected) {
      if (config.controllerSelected && config.controllerItem) {
        await this.removeCartLine(config.controllerItem.key);
      }

      if (config.xlrItem) {
        await this.removeCartLine(config.xlrItem.key);
      }

      return;
    }

    await this.addCartItem(
      config.xlrVariantId,
      1,
      {
        _rc_accessory: 'xlr',
        _rc_parent_config_id: config.configId,
        _rc_parent_car: config.shortCar
      }
    );
  }

  /* =========================================================
     XLR SMART CONTROLLER TOGGLE
     ========================================================= */

  async toggleController(config) {
    if (!config.xlrSelected) {
      return;
    }

    if (config.controllerSelected) {
      if (config.controllerItem) {
        await this.removeCartLine(config.controllerItem.key);
      }

      return;
    }

    if (!config.controllerVariantId) {
      throw new Error('Smart Controller-variant saknas');
    }

    await this.addCartItem(
      config.controllerVariantId,
      1,
      {
        _rc_accessory: 'xlr-controller',
        _rc_parent_config_id: config.configId,
        _rc_parent_car: config.shortCar
      }
    );
  }

  /* =========================================================
     REPLACE MAIN VARIANT
     ========================================================= */

  async replaceMainVariant(oldItem, newVariantId, properties) {
    if (!oldItem?.key) {
      throw new Error('Befintlig produkt saknas');
    }

    if (!newVariantId) {
      throw new Error('Ny variant saknas');
    }

    await this.removeCartLine(oldItem.key);

    await this.addCartItem(
      newVariantId,
      oldItem.quantity,
      properties
    );
  }

  /* =========================================================
     AJAX HELPERS
     ========================================================= */

  async addCartItem(variantId, quantity = 1, properties = {}) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(quantity),
        properties
      })
    });

    if (!response.ok) {
      let message = 'Kunde inte lägga till produkten';

      try {
        const error = await response.json();

        if (error?.description) {
          message = error.description;
        }
      } catch (error) {
        /* ignore */
      }

      throw new Error(message);
    }

    return await response.json();
  }

  async removeCartLine(key) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error('Kunde inte ta bort produkten');
    }

    return await response.json();
  }

  /* =========================================================
     QUANTITY / DELETE
     ========================================================= */

  bindItemControls() {
    this.productContainer
      .querySelectorAll('.rc-drawer-qty-button')
      .forEach((button) => {
        button.addEventListener('click', async () => {
          const key = button.dataset.key;

          const currentQuantity =
            parseInt(button.dataset.quantity, 10) || 0;

          const quantity =
            button.dataset.action === 'increase'
              ? currentQuantity + 1
              : Math.max(0, currentQuantity - 1);

          await this.changeCartItem(key, quantity);
        });
      });

    this.productContainer
      .querySelectorAll('.rc-drawer-remove')
      .forEach((button) => {
        button.addEventListener('click', async () => {
          await this.changeCartItem(button.dataset.key, 0);
        });
      });
  }

  async changeCartItem(key, quantity) {
    try {
      this.notification.classList.add('rc-cart-loading');

      window.__raceChipDrawerMutation = true;

      let response;

      if (Number(quantity) === 0) {
        const cart = await this.getCart();
        const targetItem = cart.items.find((item) => item.key === key);

        if (targetItem) {
          const properties = targetItem.properties || {};
          const family = properties._rc_family || '';

          const isMain =
            family === 's' ||
            family === 'rs' ||
            family === 'gts-5' ||
            family === 'gts-5-black' ||
            family === 'xlr5';

          const configId = String(
            properties._rc_config_id ||
            properties._rc_base_variant_id ||
            targetItem.variant_id
          );

          const parentConfigId = String(
            properties._rc_parent_config_id || ''
          );

          const updates = {
            [targetItem.key]: 0
          };

          if (isMain) {
            cart.items.forEach((item) => {
              const itemParentConfigId = String(
                item.properties?._rc_parent_config_id || ''
              );

              if (
                itemParentConfigId &&
                itemParentConfigId === configId
              ) {
                updates[item.key] = 0;
              }
            });
          }

          if (this.isXlrItem(targetItem) && parentConfigId) {
            cart.items.forEach((item) => {
              const itemParentConfigId = String(
                item.properties?._rc_parent_config_id || ''
              );

              if (
                this.isControllerItem(item) &&
                itemParentConfigId === parentConfigId
              ) {
                updates[item.key] = 0;
              }
            });
          }

          response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ updates })
          });
        }
      }

      if (!response) {
        response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: key,
            quantity
          })
        });
      }

      if (!response.ok) {
        throw new Error('Kunde inte uppdatera varukorgen');
      }

      await this.refreshCart();
    } catch (error) {
      console.error('RaceChip cart update:', error);
    } finally {
      window.__raceChipDrawerMutation = false;

      this.notification.classList.remove('rc-cart-loading');
    }
  }

  /* =========================================================
     CART COUNT
     ========================================================= */

  updateCartBubble(count) {
    const cartIcon = document.getElementById('cart-icon-bubble');

    if (!cartIcon) return;

    let bubble = cartIcon.querySelector('.cart-count-bubble');

    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        cartIcon.appendChild(bubble);
      }

      bubble.innerHTML = `
        <span aria-hidden="true">${count}</span>
        <span class="visually-hidden">
          ${count} produkter i varukorgen
        </span>
      `;
    } else if (bubble) {
      bubble.remove();
    }
  }

  /* =========================================================
     HELPERS
     ========================================================= */

  money(cents) {
    try {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${Math.round(Number(cents || 0) / 100)} kr`;
    }
  }

  escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  /* =========================================================
     OPEN / CLOSE
     ========================================================= */

  open() {
    if (!this.notification) return;

    this.notification.classList.add('animate', 'active');

    document.body.classList.add('rc-cart-drawer-open');

    if (
      this.header &&
      typeof this.header.reveal === 'function'
    ) {
      this.header.reveal();
    }

    this.notification.focus();

    this.dispatchCartViewEvent();
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove('active');

    document.body.classList.remove('rc-cart-drawer-open');
  }

  /* =========================================================
     SHOPIFY COMPATIBILITY
     ========================================================= */

  async renderContents(parsedState) {
    this.cartItemKey = parsedState?.key;

    try {
      await this.refreshCart();
    } catch (error) {
      console.error('RaceChip cart drawer:', error);
    }

    this.bindCartIcon();

    if (
      this.header &&
      typeof this.header.reveal === 'function'
    ) {
      this.header.reveal();
    }

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = parsedDocument.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};

    if (!CartViewEvent) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart)
        })
      );
    } catch (error) {
      /*
        Analytics får aldrig stoppa varukorgen.
      */
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}